import { useTranslation } from "@privacy/translations";
import { Stack, Dialog, Box, Typography, Button, Link, DialogTitle, DialogActions, DialogContent } from "@mui/material";
import { CircularProgress } from "@privacy/ui";
import { useState } from "react";
import { CircleQuestionMarkIcon } from "../../shared/icons";
import { newsQueryOptions, markAsReadNewsMutationOptions } from "../../api/news";
import { type UserInfo, useUserInfo } from "../../api/userInfo";
import { usePermission } from "../../shared/permissions";
import { BaseAccordionMenu, SidebarItem } from "./SidebarMenuItem";
import { customScrollbar } from "../../shared/customScrollbar";
import { useMutation, useQuery } from "@tanstack/react-query";
import { prefetch } from "../../api/prefetch";

export const HelpMenuAccordion = () => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const { hasPermission } = usePermission();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dialogState, setDialogState] = useState<"news" | "support" | null>(() =>
    !hasPermission("organization-api:news:read") || userInfo.isE2EUser || userInfo.newsInfo.hasReadLatest
      ? null
      : "news"
  );

  return (
    <>
      <BaseAccordionMenu
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        label={t("Sidebar.Help")}
        Icon={CircleQuestionMarkIcon}
      >
        <SidebarItem component="button" isNested label={t("Support")} onClick={() => setDialogState("support")} />
        <SidebarItem
          onHover={() => prefetch(newsQueryOptions())}
          component="button"
          isNested
          label={t("News")}
          onClick={() => setDialogState("news")}
        />
      </BaseAccordionMenu>
      {dialogState === "support" && <SupportDialog onClose={() => setDialogState(null)} />}
      {dialogState === "news" && <NewsDialog userNewsInfo={userInfo.newsInfo} onClose={() => setDialogState(null)} />}
    </>
  );
};

type NewsDialogProps = {
  onClose: () => void;
  userNewsInfo: UserInfo["newsInfo"];
};

const NewsDialog = ({ onClose, userNewsInfo }: NewsDialogProps) => {
  const { t, formatDate } = useTranslation();
  const newsQuery = useQuery(newsQueryOptions());
  const markAsReadNewsMutation = useMutation(markAsReadNewsMutationOptions());

  const handleClose = () => {
    onClose();
    !userNewsInfo.hasReadLatest && markAsReadNewsMutation.mutate();
  };

  return (
    <Dialog open onClose={handleClose} slotProps={{ paper: { sx: { minWidth: "45rem", maxHeight: "98vh" } } }}>
      <DialogTitle>{t("News.Dialog.Header")}</DialogTitle>
      <DialogContent sx={{ ...customScrollbar, height: "60vh" }}>
        <Stack spacing={6} sx={{ height: "100%" }}>
          {!newsQuery.data ? (
            <Stack spacing={1} sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}>
              <CircularProgress />
            </Stack>
          ) : (
            newsQuery.data.map(n => (
              <Stack spacing={1} key={n.id}>
                <Stack spacing={2} direction={"row"} sx={{ alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: theme => theme.typography.pxToRem(12),
                      color: theme => theme.palette.text.secondary,
                    }}
                  >
                    {formatDate(n.createdAt, {})}
                  </Typography>
                  {(!userNewsInfo.newsReadDate || new Date(n.createdAt) > new Date(userNewsInfo.newsReadDate)) && (
                    <Box
                      sx={{
                        borderRadius: 1,
                        bgcolor: theme => theme.palette.warning["400"],
                        display: "flex",
                        alignItems: "center",
                        px: 0.5,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: theme => theme.typography.pxToRem(12),
                          fontWeight: 700,
                          color: theme => theme.palette.background.paper,
                        }}
                      >
                        {t("News.New")}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                <Typography sx={{ textWrap: "balance" }} variant="h3">
                  {n.header}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", color: theme => theme.palette.text.secondary }}
                >
                  {n.body}
                </Typography>
              </Stack>
            ))
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={markAsReadNewsMutation.isPending} onClick={handleClose}>
          {t("Support.Dialog.Close.Button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type SupportDialogProps = { onClose: () => void };

const SupportDialog = ({ onClose }: SupportDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onClose} slotProps={{ paper: { sx: { minWidth: "40rem" } } }}>
      <DialogTitle>{t("Support.Dialog.Header")}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">{t("Support.Dialog.Info.Content")}</Typography>
          <Link target="_blank" href={"https://draftit.se/knowledge"}>
            {t("Support.Dialog.Link.KnowledgeSharing")}
          </Link>
        </Stack>
        <Stack spacing={6} sx={{ mt: 6 }}>
          <Stack spacing={1}>
            <Typography variant="h3">{t("Support.Dialog.Mail.Header")}</Typography>
            <Link href={`mailto:${t("Support.Dialog.Mail.Content")}`}>{t("Support.Dialog.Mail.Content")}</Link>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="h3">{t("Support.Dialog.Phone.Header")}</Typography>
            <Typography variant="body1">{t("Support.Dialog.Phone.Content")}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Support.Dialog.Close.Button")}</Button>
      </DialogActions>
    </Dialog>
  );
};
